import { useState } from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { BrandMark, BRAND_PROGRAM } from "../components/Brand";

const Login = () => {
  const { user, login, register, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";

  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to={from} replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Please enter your email and password.");
      return;
    }
    if (mode === "register" && password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setSubmitting(true);
    const result = mode === "login"
      ? await login(email, password)
      : await register(email, password, name);
    setSubmitting(false);
    if (result.ok) {
      toast.success(mode === "login" ? `Welcome back, ${result.user.name || result.user.email}` : "Account created");
      navigate(from, { replace: true });
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="login-page">
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="inline-flex items-center gap-3 mb-8">
            <BrandMark className="h-10 w-auto" />
            <span className="h-7 w-px bg-[#E8E2D9]" aria-hidden="true" />
            <Link
              to="/"
              className="font-serif-display text-base text-[#2D2A26] font-semibold hover:text-[#C86B5E] transition-colors"
            >
              {BRAND_PROGRAM}
            </Link>
          </div>

          <div className="bg-white border border-[#E8E2D9] rounded-3xl p-8 sm:p-10 shadow-[0_10px_40px_rgba(0,0,0,0.05)]">
            <h1 className="font-serif-display text-3xl sm:text-4xl text-[#2D2A26] font-semibold leading-tight">
              {mode === "login" ? "Welcome back." : "Create your account."}
            </h1>
            <p className="mt-2 text-sm text-[#5C5A56]">
              {mode === "login"
                ? "Sign in to take the quiz and view your results."
                : "An account lets you save your results, download a PDF, and invite a partner."}
            </p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-4">
              {mode === "register" && (
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#8A8782] mb-2">
                    Your name (optional)
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={80}
                    data-testid="register-name-input"
                    autoComplete="name"
                    className="w-full px-5 py-3 rounded-full border-2 border-[#E8E2D9] bg-white text-[#2D2A26] focus:outline-none focus:border-[#C86B5E] transition-colors"
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#8A8782] mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  autoComplete="email"
                  className="w-full px-5 py-3 rounded-full border-2 border-[#E8E2D9] bg-white text-[#2D2A26] focus:outline-none focus:border-[#C86B5E] transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#8A8782] mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={mode === "register" ? 6 : 1}
                    data-testid="login-password-input"
                    autoComplete={mode === "login" ? "current-password" : "new-password"}
                    className="w-full px-5 py-3 pr-14 rounded-full border-2 border-[#E8E2D9] bg-white text-[#2D2A26] focus:outline-none focus:border-[#C86B5E] transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    data-testid="toggle-password-visibility"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full hover:bg-[#FDF5EE] flex items-center justify-center text-[#8A8782] hover:text-[#C86B5E] transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" strokeWidth={2} />
                    ) : (
                      <Eye className="w-4 h-4" strokeWidth={2} />
                    )}
                  </button>
                </div>
                {mode === "register" && (
                  <p className="mt-2 text-xs text-[#8A8782]">At least 6 characters.</p>
                )}
                {mode === "login" && (
                  <div className="mt-2 text-right">
                    <Link
                      to="/forgot-password"
                      data-testid="forgot-password-link"
                      className="text-xs font-semibold text-[#C86B5E] hover:underline"
                    >
                      Forgot password?
                    </Link>
                  </div>
                )}
              </div>

              {error && (
                <div
                  className="rounded-2xl bg-[#FDF5EE] border border-[#F8EBE9] p-3 text-sm text-[#C86B5E]"
                  data-testid="login-error"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="login-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#C86B5E] text-white rounded-full px-7 py-3.5 font-medium hover:bg-[#B35B4E] disabled:opacity-60 transition-all shadow-sm hover:shadow-md active:scale-[0.99]"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    {mode === "login" ? "Sign in" : "Create account"}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center text-sm text-[#5C5A56]">
              {mode === "login" ? (
                <>
                  Don’t have an account?{" "}
                  <button
                    onClick={() => { setMode("register"); setError(""); }}
                    data-testid="switch-to-register"
                    className="text-[#C86B5E] font-semibold hover:underline"
                  >
                    Sign up
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{" "}
                  <button
                    onClick={() => { setMode("login"); setError(""); }}
                    data-testid="switch-to-login"
                    className="text-[#C86B5E] font-semibold hover:underline"
                  >
                    Sign in
                  </button>
                </>
              )}
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-[#8A8782]">
            This quiz is for reflection and education only. Not a clinical diagnosis or a substitute for therapy.
          </p>
        </motion.div>
      </main>
    </div>
  );
};

export default Login;
